import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Landmark, Film, MapPin, TrendingUp, Clock, CheckCircle2, ChevronRight } from "lucide-react";

const PLAN_LABEL = {
  indie_sandbox: "Indie Sandbox",
  regional:      "Regional",
  ad_film:       "Ad Film",
  feature:       "Feature",
};

const STATUS_META = {
  pre_production: { label: "Pre-Production", color: "#8B8F96", icon: Clock },
  in_production:  { label: "In Production",  color: "#F5B82E", icon: TrendingUp },
  post:           { label: "Post",           color: "#6B4EFF", icon: Film },
  wrapped:        { label: "Wrapped",        color: "#10B981", icon: CheckCircle2 },
};

/** Investor Slate View — LP-style dashboard.
 *  Groups backed projects by executing PH, exposes wrap milestones.
 */
export default function InvestorSlatePanel() {
  const [slate, setSlate] = useState({ groups: [], total_projects: 0 });
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/me/investor-slate").then(({ data }) => setSlate(data)).catch(() => {});
  }, []);

  const totalWrapped = slate.groups.reduce(
    (n, g) => n + g.projects.filter((p) => p.is_wrapped).length, 0);

  const groupsFiltered = slate.groups.map((g) => ({
    ...g,
    projects: g.projects.filter((p) => {
      if (filter === "all") return true;
      if (filter === "wrapped") return p.is_wrapped;
      return p.status === filter;
    }),
  })).filter((g) => g.projects.length > 0);

  return (
    <section data-testid="investor-slate-panel">
      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55 flex items-center gap-2">
            <Landmark size={11} className="text-[#F5B82E]" /> Investor · Slate View
          </div>
          <h2 className="font-display text-3xl uppercase tracking-tight mt-1">Your Slate</h2>
          <div className="mt-1 text-xs text-white/55">
            {slate.total_projects} project{slate.total_projects === 1 ? "" : "s"} across{" "}
            {slate.groups.length} executing house{slate.groups.length === 1 ? "" : "s"}
            {totalWrapped > 0 && <> · <span className="text-emerald-300">{totalWrapped} wrapped</span></>}
          </div>
        </div>
        <div className="flex gap-1.5" data-testid="slate-filter">
          {[
            { k: "all",             l: "All" },
            { k: "pre_production",  l: "Pre" },
            { k: "in_production",   l: "Live" },
            { k: "post",            l: "Post" },
            { k: "wrapped",         l: "Wrapped" },
          ].map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)}
                    data-testid={`slate-filter-${f.k}`}
                    className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em] border transition-all
                      ${filter === f.k
                        ? "border-[#F5B82E]/60 bg-[#F5B82E]/10 text-[#F5B82E]"
                        : "border-white/10 text-white/55 hover:border-white/25"}`}>
              {f.l}
            </button>
          ))}
        </div>
      </header>

      {groupsFiltered.length === 0 ? (
        <div className="mt-8 cs-card rounded-2xl px-6 py-14 text-center">
          <Landmark size={28} className="mx-auto text-white/40 mb-3" />
          <div className="text-sm text-white/60">
            {slate.total_projects === 0
              ? "No projects yet — create one to start backing your first title."
              : "No projects match this filter."}
          </div>
        </div>
      ) : (
        <div className="mt-6 space-y-8" data-testid="slate-groups">
          {groupsFiltered.map((g) => (
            <div key={g.executing_ph_id} data-testid={`slate-group-${g.executing_ph_id}`}>
              <div className="flex items-baseline justify-between mb-3">
                <div className="font-display text-lg uppercase tracking-tight">{g.executing_ph_name}</div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-white/45">
                  {g.projects.length} project{g.projects.length === 1 ? "" : "s"}
                  {g.executing_ph_type && g.executing_ph_type !== "production_house" &&
                    <> · <span className="text-white/60">{g.executing_ph_type.replace("_", " ")}</span></>}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {g.projects.map((p) => <ProjectTile key={p.id} p={p} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ProjectTile({ p }) {
  const meta = STATUS_META[p.status] || STATUS_META.pre_production;
  const Icon = meta.icon;
  return (
    <Link to={`/projects/${p.id}`}
      className="group cs-card rounded-xl p-4 hover:border-white/25 transition-all"
      data-testid={`slate-project-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-display text-base uppercase tracking-tight truncate">{p.title}</div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-white/50 mt-0.5">
            {PLAN_LABEL[p.plan] || p.plan}
            {p.location && <> · <MapPin size={9} className="inline mb-0.5" /> {p.location}</>}
          </div>
        </div>
        <ChevronRight size={14} className="text-white/30 group-hover:text-white/60 shrink-0 mt-0.5" />
      </div>
      <div className="mt-3 flex items-center gap-1.5" style={{ color: meta.color }}>
        <Icon size={11} />
        <span className="text-[10px] tracking-[0.22em] uppercase">{meta.label}</span>
      </div>
    </Link>
  );
}
